import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  SelectOptionType,
  MarketingActivityInitialState,
  CreatedActivityResponseType,
} from '../utils/Types';
import { isAPISuccess } from '../utils/Util';
import {
  API_STATUS,
  BlankPagination,
  DEFAULT_OPTION,
  DEFAULT_OPTION_LOCATION_SET_TARGET,
} from '../utils/Constant';
import {
  addAssigneesMarketingActivity,
  approveRejectMarketingActivity,
  createNewMarketingActivity,
  getDetailedMarketingActivity,
  getMarketingActivities,
  getMarketingEmployeeList,
  removeAssigneeMarketingActivity,
  updatePrimaryEmployee,
} from '../services/marketingActivityService';
import { moduleIds } from '../pages/create-activity/activityHelper';
import {
  getStatusDrop,
  getMasterTargetsDropValues,
  getTargetsDropValues,
} from '../services/dropDownService';
export const initialState: MarketingActivityInitialState = {
  yearOptions: [],
  seasonOptions: [DEFAULT_OPTION],
  employeeList: [],
  marketingTypes: [DEFAULT_OPTION],
  marketingActivityList: {
    pagination: BlankPagination,
    result: [],
    status: '',
  },
  statusOptions: [DEFAULT_OPTION_LOCATION_SET_TARGET],
};

export const getEmployeeList = createAsyncThunk(
  'marketingActivity/employeeList',
  async (params: any) => {
    const res = await getMarketingEmployeeList(params);
    return res.data;
  }
);

export const fetchTargetDrops = createAsyncThunk(
  'marketingActivity/dropOptions',
  async (params: any) => {
    const res = await getTargetsDropValues(params);
    return res.data;
  }
);

export const getMarketingActivityList = createAsyncThunk(
  'marketingActivity/getMarketingActivityList',
  async (params: any) => {
    const res = await getMarketingActivities(params);
    return res.data;
  }
);

export const approveOrReject = createAsyncThunk(
  'marketingActivity/ApproveOrReject',
  async (data: any) => {
    const res = await approveRejectMarketingActivity(data);
    return res.data;
  }
);

export const getYearSeasonOptions = createAsyncThunk(
  'marketingActivity/getYearSeasonOptions',
  async () => {
    const res = await getMasterTargetsDropValues();
    return res.data;
  }
);

export const getStatusDrops = createAsyncThunk(
  'marketingActivity/getStatusDrops',
  async (params: any) => {
    const res = await getStatusDrop(params);
    return res.data;
  }
);

export const createMarketingActivity = createAsyncThunk<
  CreatedActivityResponseType,
  any
>('marketingActivity/createActivity', async (data: any) => {
  const res = await createNewMarketingActivity(data);
  return res.data;
});

export const getMarketingActivityDetails = createAsyncThunk(
  'marketingActivity/marketingActivityDetails',
  async (params: any) => {
    const res = await getDetailedMarketingActivity(params);
    return res.data;
  }
);

export const removeAssinee = createAsyncThunk(
  'marketingActivity/removeAssignee',
  async (data: any) => {
    const res = await removeAssigneeMarketingActivity(data);
    return res.data;
  }
);
export const addAssignees = createAsyncThunk(
  'marketingActivity/addAssignees',
  async (data: any) => {
    const res = await addAssigneesMarketingActivity(data);
    return res.data;
  }
);

export const changePrimaryEmployee = createAsyncThunk(
  'marketingActivity/changePrimaryEmployee',
  async (data: any) => {
    const res = await updatePrimaryEmployee(data);
    return res.data;
  }
);

export const marketingActivitySlice = createSlice({
  name: 'marketingActivity',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getEmployeeList.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          state.employeeList = action.payload.result;
        } else {
          state.employeeList = [];
        }
      })
      .addCase(fetchTargetDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const result = action.payload.result;
          if (result && result.length > 0) {
            const selectOptions: SelectOptionType[] = [];

            result.forEach(
              (element: { picklistId: number; picklistName: string }) => {
                selectOptions.push({
                  value: element.picklistId,
                  label: element.picklistName,
                });
              }
            );
            switch (action.meta.arg.module) {
              case moduleIds.seasons:
                state.seasonOptions = selectOptions;
                break;
              case moduleIds.marketingTypes:
                state.marketingTypes = selectOptions;

                break;

              default:
                break;
            }
          }
        }
      })
      .addCase(getYearSeasonOptions.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          state.yearOptions = action.payload.result?.financialYear;
          const seasons = action.payload.result?.season;
          if (seasons.length > 0) {
            const selectOptions: SelectOptionType[] = [];

            seasons.forEach(
              (element: { seasonId: number; seasonName: string }) => {
                selectOptions.push({
                  value: element.seasonId,
                  label: element.seasonName,
                });
              }
            );
            state.seasonOptions = selectOptions;
          }
        }
      })
      .addCase(getMarketingActivityList.pending, (state) => {
        state.marketingActivityList.status = API_STATUS.LOADING;
      })
      .addCase(getMarketingActivityList.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          state.marketingActivityList.pagination = action.payload.pagination;
          state.marketingActivityList.result = action.payload.result;
          state.marketingActivityList.status = API_STATUS.SUCCEEDED;
        } else {
          state.marketingActivityList.pagination = BlankPagination;
          state.marketingActivityList.result = [];
          state.marketingActivityList.status = API_STATUS.FAILED;
        }
      })
      .addCase(getStatusDrops.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          const dropdowns = action.payload.result?.map((item: any) => ({
            value: item.statusId,
            label: item.statusName,
          }));
          dropdowns.unshift(DEFAULT_OPTION_LOCATION_SET_TARGET);
          state.statusOptions = dropdowns;
        } else {
          state.statusOptions = [DEFAULT_OPTION];
        }
      });
  },
});

export const marketingActivitySelector = (state: {
  marketingActivity: MarketingActivityInitialState;
}) => state.marketingActivity;

export default marketingActivitySlice.reducer;
